import { DateRange, PeriodSegment, TimelineItemWithDisplay } from '../../models/timeline-calendar.types';
import { ROW_HEIGHT_PX } from '../timeline-calendar/constants';
import type { TimelineDiffItemState } from '../timeline-calendar/types';
import {
  TimelineColorRGBA,
  TimelineRenderFrame,
  TimelineRenderItem,
  TimelineStyleToken,
  WeekendStripe,
} from './types';

interface BuildTimelineRenderFrameParams {
  width: number;
  height: number;
  dpr: number;
  periodSegments: PeriodSegment[];
  dateRange: DateRange;
  hoveredItemId: string | null;
  selectedItemId: string | null;
  editingItemId: string | null;
  movePreviewItemId: string | null;
  resizePreviewItemId: string | null;
  renderedItems: Array<{
    item: TimelineItemWithDisplay;
    row: number;
    pointLabelSection: number;
    position: { left: string; width: string; isVisible: boolean };
    isPending: boolean;
    showResizeHandles: boolean;
    previewState?: TimelineDiffItemState;
  }>;
  gridLinesY?: number[];
}

const parsePercentToPx = (percentage: string, width: number): number => {
  const numeric = Number.parseFloat(percentage.replace('%', ''));
  if (!Number.isFinite(numeric)) {
    return 0;
  }
  return (numeric / 100) * width;
};

const parseHexColor = (value: string): TimelineColorRGBA | null => {
  const normalized = value.trim();
  const short = /^#([0-9a-f]{3})$/i.exec(normalized);
  if (short) {
    const [r, g, b] = short[1].split('').map((digit) => Number.parseInt(`${digit}${digit}`, 16) / 255);
    return [r, g, b, 1];
  }

  const long = /^#([0-9a-f]{6})([0-9a-f]{2})?$/i.exec(normalized);
  if (long) {
    const rgbHex = long[1];
    const alphaHex = long[2];
    const r = Number.parseInt(rgbHex.slice(0, 2), 16) / 255;
    const g = Number.parseInt(rgbHex.slice(2, 4), 16) / 255;
    const b = Number.parseInt(rgbHex.slice(4, 6), 16) / 255;
    const a = alphaHex ? (Number.parseInt(alphaHex, 16) / 255) : 1;
    return [r, g, b, a];
  }

  return null;
};

const parseRgbColor = (value: string): TimelineColorRGBA | null => {
  const match = /^rgba?\(([^)]+)\)$/i.exec(value.trim());
  if (!match) {
    return null;
  }

  const parts = match[1].split(',').map((part) => part.trim());
  if (parts.length < 3) {
    return null;
  }

  const r = Number.parseFloat(parts[0]) / 255;
  const g = Number.parseFloat(parts[1]) / 255;
  const b = Number.parseFloat(parts[2]) / 255;
  const a = parts[3] === undefined ? 1 : Number.parseFloat(parts[3]);
  if (![r, g, b, a].every((channel) => Number.isFinite(channel))) {
    return null;
  }

  const clamp = (channel: number) => Math.max(0, Math.min(1, channel));
  return [clamp(r), clamp(g), clamp(b), clamp(a)];
};

const parseItemColor = (value: string, fallback: TimelineColorRGBA): TimelineColorRGBA => {
  return parseHexColor(value) ?? parseRgbColor(value) ?? fallback;
};

const isPointItem = (item: TimelineItemWithDisplay): boolean => {
  return !item.endDate;
};

const resolveStyleToken = (params: {
  itemId: string;
  isPending: boolean;
  previewState?: TimelineDiffItemState;
  hoveredItemId: string | null;
  selectedItemId: string | null;
  editingItemId: string | null;
  movePreviewItemId: string | null;
  resizePreviewItemId: string | null;
}): TimelineStyleToken => {
  if (params.isPending) {
    return 'preview';
  }
  if (params.itemId === params.resizePreviewItemId) {
    return 'resizing';
  }
  if (params.itemId === params.movePreviewItemId) {
    return 'moving';
  }
  if (params.itemId === params.editingItemId) {
    return 'editing';
  }
  if (params.itemId === params.selectedItemId) {
    return 'selected';
  }
  if (params.itemId === params.hoveredItemId) {
    return 'hovered';
  }
  if (params.previewState === 'added') {
    return 'preview-added';
  }
  if (params.previewState === 'existing') {
    return 'preview-muted';
  }
  return 'normal';
};

const YEARLY_THRESHOLD_DAYS = 730;

const buildDayGridLines = (dateRange: DateRange, width: number): number[] => {
  const startMs = dateRange.start.getTime();
  const endMs = dateRange.end.getTime();
  const spanMs = endMs - startMs;
  if (spanMs <= 0) {
    return [];
  }

  // 表示期間が5ヶ月（約150日）を超える場合は日メモリを表示しない
  const spanDays = spanMs / 86_400_000;
  if (spanDays > 150) {
    return [];
  }

  const lines: number[] = [];
  const cursor = new Date(dateRange.start);
  cursor.setHours(0, 0, 0, 0);
  // 翌日の0時から開始
  cursor.setDate(cursor.getDate() + 1);

  while (cursor.getTime() < endMs) {
    const cursorMs = cursor.getTime();
    const x = ((cursorMs - startMs) / spanMs) * width;
    lines.push(x);
    cursor.setDate(cursor.getDate() + 1);
  }

  return lines;
};

const buildWeekendStripes = (dateRange: DateRange, width: number): WeekendStripe[] => {
  const startMs = dateRange.start.getTime();
  const endMs = dateRange.end.getTime();
  const spanMs = endMs - startMs;
  if (spanMs <= 0) {
    return [];
  }

  // 日単位の縦線と同じ条件で土日ストライプを表示する
  const spanDays = spanMs / 86_400_000;
  if (spanDays > 150) {
    return [];
  }

  const stripes: WeekendStripe[] = [];

  // Find the first Saturday on or after dateRange.start (using local time)
  const cursor = new Date(dateRange.start);
  cursor.setHours(0, 0, 0, 0);
  const dayOfWeek = cursor.getDay();
  const daysUntilSat = (6 - dayOfWeek + 7) % 7;
  cursor.setDate(cursor.getDate() + daysUntilSat);

  while (cursor.getTime() < endMs) {
    const satStartMs = cursor.getTime();
    // Weekend spans Saturday + Sunday = 2 days
    const sunEnd = new Date(cursor);
    sunEnd.setDate(sunEnd.getDate() + 2);
    const sunEndMs = sunEnd.getTime();

    const visibleStart = Math.max(satStartMs, startMs);
    const visibleEnd = Math.min(sunEndMs, endMs);

    if (visibleStart < visibleEnd) {
      const x = ((visibleStart - startMs) / spanMs) * width;
      const stripeWidth = ((visibleEnd - visibleStart) / spanMs) * width;
      stripes.push({ x, width: stripeWidth });
    }

    // Advance to next Saturday (+7 days)
    cursor.setDate(cursor.getDate() + 7);
  }

  return stripes;
};

export const buildTimelineRenderFrame = ({
  width,
  height,
  dpr,
  periodSegments,
  dateRange,
  hoveredItemId,
  selectedItemId,
  editingItemId,
  movePreviewItemId,
  resizePreviewItemId,
  renderedItems,
  gridLinesY: gridLinesYParam,
}: BuildTimelineRenderFrameParams): TimelineRenderFrame => {
  const safeWidth = Math.max(1, width);
  const safeHeight = Math.max(1, height);
  const safeDpr = Number.isFinite(dpr) && dpr > 0 ? dpr : 1;

  const renderItems: TimelineRenderItem[] = renderedItems
    .filter(({ position }) => position.isVisible)
    .map(({ item, row, pointLabelSection, position, isPending, showResizeHandles, previewState }) => {
      const pointItem = isPointItem(item);
      const pointRadius = 6;
      const rawX = parsePercentToPx(position.left, safeWidth);
      const x = pointItem ? rawX - pointRadius : rawX;
      const widthPx = pointItem ? pointRadius * 2 : Math.max(1, parsePercentToPx(position.width, safeWidth));
      const y = row * ROW_HEIGHT_PX + 10 + (pointItem ? pointLabelSection * 28 : 0);
      const heightPx = pointItem ? 12 : 14;
      const fillColor = parseItemColor(item.color.bg, [0.23, 0.51, 0.96, 0.88]);
      const borderColor = parseItemColor(item.color.border, [0.16, 0.39, 0.92, 1]);

      const style = resolveStyleToken({
        itemId: item.id,
        isPending,
        previewState,
        hoveredItemId,
        selectedItemId,
        editingItemId,
        movePreviewItemId,
        resizePreviewItemId,
      });

      return {
        id: item.id,
        sectionId: item.sectionId ?? '',
        x,
        y,
        width: widthPx,
        height: heightPx,
        isPoint: pointItem,
        fillColor,
        borderColor,
        style,
        showResizeHandles,
        canResize: true,
      };
    });

  const startMs = dateRange.start.getTime();
  const endMs = dateRange.end.getTime();
  const todayMs = Date.now();
  const todayMarkerX = todayMs >= startMs && todayMs <= endMs && endMs > startMs
    ? ((todayMs - startMs) / (endMs - startMs)) * safeWidth
    : null;

  return {
    width: safeWidth,
    height: safeHeight,
    dpr: safeDpr,
    gridLinesX: (() => {
      const dayLines = buildDayGridLines(dateRange, safeWidth);
      return dayLines.length > 0
        ? dayLines
        : periodSegments.map((segment) => (segment.leftPct / 100) * safeWidth);
    })(),
    gridLinesY: gridLinesYParam ?? [],
    weekendStripes: buildWeekendStripes(dateRange, safeWidth),
    items: renderItems,
    todayMarkerX,
  };
};
